import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Collapse } from '@mui/material';
import CollapsibleButton from '../../SharedComponents/CollapsibleButton';
import DigInSummaryItem from '../../SharedComponents/DigInSummaryItem';
import DurationSummaryItem from '../../SharedComponents/DurationSummaryItem';
import TicketSummary from '../../TicketDetailComponents/SubComponents/TicketSummary/TicketSummary';
// import SingleTicketActions from '../SingleTicketActions';
import WorkLog from '../../TicketDetailComponents/SubComponents/WorkLog/WorkLog';
import PositiveResponse from '../../TicketDetailComponents/SubComponents/PositiveResponse/PositiveResponse';
// import TicketAction from '../TicketAction'; // Component doesn't exist
import MeetUp from '../../TicketDetailComponents/SubComponents/MeetUp/MeetUp';
// import DigInRisk from '../../TicketDetailComponents/SubComponents/DigInRisk/DigInRisk';
import './style.scss';
// import { Link } from 'react-router-dom';
import { fetchPresignedUrl } from '../../../utils/helpers/fetchPresignedUrl';
import { getFolderByID } from '../../../utils/helpers';
import { Store } from '../../../store';
import { DigInRisk } from '../../TicketDetailComponents/SubComponents';
const EXCLUDED_FIELDS = {
  CustomerDetails: [
    'CityTownPlace',
    'Company',
    'Email',
    'ExcavatorID',
    'Language',
    'Mobile',
    'Name',
    'Phone',
    'State',
    'StreetAddress',
    'UserIndustry',
    'UserType',
    'Zipcode',
  ],
  LocationDetails: [
    'AddressLocation',
    'CityTownPlace',
    'County',
    'ExcavatorRemarks',
    'Latitude',
    'Longitude',
    'NearbyCrossStreet',
    'OnsiteContactName',
    'OnsiteContactPhone',
    'NearbyCrossStreet',
    'State',
    'StreetSidewalk',
    'TicketEditReason',
    'Zipcode',
  ],
  ReferralDetails: [
    'AnticipatedDepth',
    'Boring',
    'DelineatedMethod',
    'Explosives',
    'From',
    'IsEmergency',
    'JobNoName',
    'JobNumber',
    'LegalStartDate',
    'MessageVersionNumber',
    'PavementOnly',
    'Permit',
    'PreviousTicketNumber',
    'PreviousTicketRevNumber',
    'ProjectOwner',
    'ResponseRequired',
    'SequenceNumber',
    'StationCode',
    'SubmittedDate',
    'TicketExpirationDate',
    'TicketMedium',
    'TicketNumber',
    'TicketRevisionNumber',
    'TicketStatus',
    'TicketType',
    'To',
    'TransmissionID',
    'UtilityID',
    'UtilityName',
    'VacuumExcavation',
    'WorkActivity',
    'WorkBeginDate',
    'WorkDuration',
  ],
};

const ArchiveDetails = ({ folders, ticket, logs = {}, ticketActions, userToken, node }) => {
  const {
    docType,
    digInRiskModel,
    durationModel,
    durationModel95QR,
    legacyTicket,
    legacyWorkItem,
    printable_text,
    street,
    lanID,
    metadata,
    one_year,
    pge_workDate,
    pge_folder,
    pge_folderID,
    pge_division,
    pge_address,
    pge_responseType,
    pge_status,
    pge_statusUpdated,
  } = ticket || {};
  const { state } = useContext(Store);
  const { foldersJson } = state;

  const folderData = pge_folderID ? getFolderByID(foldersJson, pge_folderID) : null;
  const [S3PhotoArray, setS3PhotoArray] = useState([]);
  const [openLocationDetails, setOpenLocationDetails] = useState(true);
  const [openReferralDetails, setOpenReferralDetails] = useState(true);
  const [openCustomerDetails, setOpenCustomerDetails] = useState(false);

  useEffect(() => {
    // Only fetch photos if userToken is provided
    if (!userToken?.access_token || !ticket?.photos) {
      return;
    }

    const generateS3PhotoData = async () => {
      const photos = ticket.photos ? ticket.photos : [];
      const S3PhotoArray = await Promise.all(
        photos.map(async photo => {
          const presignedUrl = await fetchPresignedUrl(photo.thumbnail, userToken.access_token);
          const photoName = photo.displayName;
          return {
            presignedUrl,
            filename: photoName,
            original: photo.original,
            thumbnail: photo.thumbnail,
          };
        })
      );
      setS3PhotoArray(S3PhotoArray);
    };
    generateS3PhotoData();
  }, [ticket, userToken]);

  let printableText =
    docType === 'ticket'
      ? legacyTicket
        ? legacyTicket.TicketText
        : printable_text
          ? printable_text._
          : ''
      : '';

  const isValidDuration = durationModel && durationModel95QR;
  const durationText = isValidDuration
    ? `${Math.round(durationModel.prediction)}mins or up to ${Math.round(durationModel95QR.prediction)}mins`
    : 'Predicted duration: Not available';

  const highlightPrintText = () => {
    let parts = printableText.split(/(\+|[0-9] Year: Y+|Boring: Y\b)/gi);
    for (let i = 1; i < parts.length; i += 2) {
      parts[i] = (
        <span style={{ backgroundColor: '#FFFF00' }} key={i}>
          {parts[i]}
        </span>
      );
    }
    return <div>{parts}</div>;
  };
  logs = logs.results ? logs.results.filter(log => log.doc && log.doc.docType) : [];
  logs = logs.map(log => {
    return {
      seq: log.seq,
      id: log.id,
      doc: log.doc,
      changes: log.changes,
      folderValues:
        log.doc.value && folders
          ? folders
              .filter(folderValues => {
                return folderValues.id === log.doc.value;
              })
              .map(folderValues => {
                return {
                  division: folderValues.division,
                  folder: folderValues.name,
                  id: folderValues.id,
                };
              })
          : [],
    };
  });

  let updatedBy = logs.filter(item => item.doc && item.doc.action);

  // Return early if no ticket data
  if (!ticket) {
    return <div className="ticket-info">No ticket data available</div>;
  }

  // Map ticket data to TicketSummary format
  const ticketForSummary = {
    id: ticket._id || ticket.id,
    doc_type: docType,
    pge_excavator: ticket.pge_excavator || ticket.excavator,
    pge_work_date: pge_workDate,
    pge_status: pge_status,
    work_type: ticket.work_type || ticket.workType,
    pge_division: pge_division,
    type: ticket.type,
    fieldMeets: ticket.fieldMeets || [],
    standbys: ticket.standbys || [],
  };

  return (
    <div ref={node} className="ticket-info" id="ticket-detail-layout">
      <div className="address">{pge_address ? pge_address : street}</div>

      <div className="summary">
        <div className="summary-row">
          <div className="summary-item">
            <span className="summary-label">Division/Folder</span>
            <span>
              {folderData ? folderData.division : pge_division} -{' '}
              {folderData ? folderData.name : pge_folder}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Area of Continual Excavation</span>
            <span>{one_year && one_year === 'Y' ? 'Yes' : 'No'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Predicted Duration</span>
            <span>{durationText}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">User</span>
            {metadata ? (
              <span>
                {lanID
                  ? lanID
                  : metadata.originatingUser
                    ? metadata.originatingUser.toUpperCase()
                    : metadata.user
                      ? metadata.user.toUpperCase()
                      : ''}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        </div>
        <div className="summary-row">
          <div className="summary-item summary-item-left">
            <span className="summary-label">Ticket Start Time</span>
            <span>{pge_workDate ? moment(pge_workDate).format('MM/DD/YY HH:mm') : 'null'}</span>
          </div>
          <div className="summary-item summary-item-left">
            <span className="summary-label">Status Updated</span>
            <span>
              {pge_statusUpdated ? moment(pge_statusUpdated).format('MM/DD/YY HH:mm') : 'null'}
            </span>
          </div>
          <div className="summary-item summary-item-left">
            <span className="summary-label">Response</span>
            <span>{pge_responseType}</span>
          </div>
          <div className="summary-item summary-item-left">
            <span className="summary-label">Status:</span>
            <span>{pge_status}</span>
          </div>
        </div>
      </div>
      {ticket.type === 'RNEW' && ticket.pge_status === 'OPEN' && (
        <div className="not-cleared-row">
          <span style={{ backgroundColor: '#FFFF00' }}>&nbsp;Please review work location</span>
        </div>
      )}
      {digInRiskModel && (digInRiskModel?.metadata || digInRiskModel?.predictions) && (
        <DigInRisk digInRiskModel={digInRiskModel} />
      )}
      <div className="ticket-updatedBy">
        {updatedBy.length > 0
          ? 'Last updated by: ' +
            (updatedBy[updatedBy.length - 1].doc.metadata.originatingUser
              ? updatedBy[updatedBy.length - 1].doc.metadata.originatingUser.toUpperCase()
              : updatedBy[updatedBy.length - 1].doc.metadata.user
                ? updatedBy[updatedBy.length - 1].doc.metadata.user.toUpperCase()
                : '')
          : ''}
      </div>
      {docType === 'fieldMeet' && (
        <div>
          <div className="ticket-traversal">
            <div className="parent">PARENT TICKET:</div>
            {ticket.ticketID && (
              <a
                href={`${process.env.REACT_APP_BASE_PATH}/${process.env.REACT_APP_SUPERVISOR_TICKET_DETAILS}/${ticket.ticketID}`}
                target="_blank"
                rel="noopener noreferrer"
                className="work-ticket"
                onClick={e => e.stopPropagation()}
              >
                <div className="child">LOCATE: {ticket.ticketID}</div>
              </a>
            )}
            {legacyWorkItem && (
              <a
                href={`https://www.irth.com/Utilisphere/TicketManager/ResearchTicket.aspx?ID=${legacyWorkItem.RelatedItems[0].ItemID}`}
              >
                IRTH: {legacyWorkItem.RelatedItems[0].DisplayID}
              </a>
            )}
          </div>
          {ticket.printable_text && (
            <div className="full-ticket-text-container">{ticket.printable_text._}</div>
          )}
        </div>
      )}
      {docType === 'standby' && (
        <div>
          <div className="parent">PARENT FIELD MEET:</div>

          {ticket.fieldMeetID && (
            <a
              href={`${process.env.REACT_APP_BASE_PATH}/${process.env.REACT_APP_SUPERVISOR_TICKET_DETAILS}/${ticket.fieldMeetID}`}
              target="_blank"
              rel="noopener noreferrer"
              className="work-ticket"
              onClick={e => e.stopPropagation()}
            >
              <div className="child">LOCATE: {ticket.fieldMeetID}</div>
            </a>
          )}
          {legacyWorkItem && (
            <a
              href={`https://www.irth.com/Utilisphere/FieldProc/View.aspx?ID=${legacyWorkItem.RelatedItems[0].ItemID}`}
            >
              IRTH: {legacyWorkItem.RelatedItems[0].DisplayID}
            </a>
          )}
          {ticket.printable_text && (
            <div className="full-ticket-text-container">{ticket.printable_text._}</div>
          )}
        </div>
      )}

      {legacyTicket && (
        <a
          className="legacy-link"
          href={`https://www.irth.com/Utilisphere/TicketManager/ViewTicket.aspx?ID=${legacyTicket.UtiliSphereID}`}
          target={'_blank'}
          rel={'noreferrer'}
        >
          link to legacy ticket in IRTH
        </a>
      )}
      {legacyWorkItem && (
        <a
          className="legacy-link"
          href={`https://www.irth.com/Utilisphere/FieldProc/View.aspx?ID=${legacyWorkItem.ItemID}`}
        >
          link to legacy {docType} in IRTH
        </a>
      )}
      {docType === 'ticket' && (
        <div className="full-ticket-text-container">
          {highlightPrintText()}
          <div>
            {ticket.LocationDetails && (
              <>
                <CollapsibleButton
                  status={openLocationDetails}
                  setStatus={setOpenLocationDetails}
                  title="Location Details"
                />
                <Collapse in={openLocationDetails}>
                  <div className="collapsible-text-container">
                    {Object.keys(ticket.LocationDetails)
                      .filter(key => key !== 'WKT')
                      .map(key => {
                        return (
                          <div className="collapsible-text-content" key={key}>
                            {EXCLUDED_FIELDS['LocationDetails'].indexOf(key) > -1 ? (
                              <span>
                                {key}: {ticket.LocationDetails[key]}
                              </span>
                            ) : (
                              <span style={{ backgroundColor: '#FFFF00' }}>
                                {key}: {ticket.LocationDetails[key]}
                              </span>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </Collapse>
              </>
            )}
            {ticket.ReferralDetails && (
              <>
                <CollapsibleButton
                  status={openReferralDetails}
                  setStatus={setOpenReferralDetails}
                  title="Referral Details"
                />
                <Collapse in={openReferralDetails}>
                  <div className="collapsible-text-container">
                    {Object.keys(ticket.ReferralDetails)
                      .filter(key => key !== 'StationList')
                      .map(key => {
                        return (
                          <div className="collapsible-text-content" key={key}>
                            {EXCLUDED_FIELDS['ReferralDetails'].indexOf(key) > -1 ? (
                              <span>
                                {key}: {ticket.ReferralDetails[key]}
                              </span>
                            ) : (
                              <span style={{ backgroundColor: '#FFFF00' }}>
                                {key}: {ticket.ReferralDetails[key]}
                              </span>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </Collapse>
              </>
            )}
            {ticket.CustomerDetails && (
              <>
                <CollapsibleButton
                  status={openCustomerDetails}
                  setStatus={setOpenCustomerDetails}
                  title="Customer Details"
                />
                <Collapse in={openCustomerDetails}>
                  <div className="collapsible-text-container">
                    {Object.keys(ticket.CustomerDetails).map(key => {
                      return (
                        <div className="collapsible-text-content" key={key}>
                          {EXCLUDED_FIELDS['CustomerDetails'].indexOf(key) > -1 ? (
                            <span>
                              {key}: {ticket.CustomerDetails[key]}
                            </span>
                          ) : (
                            <span style={{ backgroundColor: '#FFFF00' }}>
                              {key}: {ticket.CustomerDetails[key]}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Collapse>
              </>
            )}
            {S3PhotoArray.map(({ presignedUrl, original }) => (
              <a href={'/photos/' + original} target="_blank" rel={'noreferrer'} key={uuidv4()}>
                <img
                  src={presignedUrl ? presignedUrl : '/image_placeholder.png'}
                  data-src={presignedUrl ? presignedUrl : '/image_placeholder.png'}
                  className="s3-image"
                  alt="Thumbnail"
                />
              </a>
            ))}
          </div>
        </div>
      )}
      {/* {logs.length > 0 &&
        logs.map((log) => {
          switch (log.doc.docType) {
            case 'workLog':
              return (
                <WorkLog
                  key={uuidv4()}
                  workLog={log.doc}
                  token={userToken?.access_token}
                />
              );
            case 'fieldMeet':
              if (docType !== 'fieldMeet') {
                return <MeetUp key={uuidv4()} meetUp={log} />;
              }
              return null;
            case 'standby':
              if (docType !== 'standby') {
                return <MeetUp key={uuidv4()} meetUp={log} />;
              }
              return null;
            case 'positiveResponse':
              return (
                <PositiveResponse key={uuidv4()} positiveResponse={log.doc} />
              );
            // case 'ticketAction':
            //   return (
            //     <TicketAction
            //       key={uuidv4()}
            //       ticketAction={log.doc}
            //       folderValues={log.folderValues[0]}
            //     />
            //   );
            case 'ticket':
              return <div key={uuidv4()} />;
            default:
              return <div key={uuidv4()}>{log.doc.docType}</div>;
          }
        })} */}

      {/* {ticketActions && <SingleTicketActions />} */}
    </div>
  );
};

ArchiveDetails.propTypes = {
  folders: PropTypes.array,
  ticket: PropTypes.object,
  logs: PropTypes.object,
  ticketActions: PropTypes.bool,
  userToken: PropTypes.object,
  node: PropTypes.object,
};

export default React.memo(ArchiveDetails);
